import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import Cookies from 'js-cookie';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

import { FASTAPI_HOST, IS_MOBILE_APP } from '../settings/config';
import StoryTypeFilter, { getStoryTypeLabel } from '../components/StoryTypeFilter';

function formatCreatedAt(dtStr) {
  try {
    let isoStr = dtStr;
    if (!isoStr) return '';

    // Replace space with T if needed
    isoStr = isoStr.replace(' ', 'T');

    // Trim microseconds to milliseconds
    if (isoStr.includes('.')) {
      isoStr = isoStr.replace(/\.(\d{3})\d+/, '.$1');
    }

    // Add Z if no timezone info
    if (!isoStr.endsWith('Z') && !isoStr.match(/[+-]\d{2}:\d{2}$/)) {
      isoStr += 'Z';
    }

    const d = new Date(isoStr);
    return isNaN(d) ? dtStr : d.toLocaleString();
  } catch {
    return dtStr;
  }
}

const StoryHistory = ({ t, showTitle = true, headerContent = null }) => {
  const PAGE_SIZE = 5;

  const [stories, setStories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [storyType, setStoryType] = useState(null);

  const [error, setError] = useState('');
  const [authWarning, setAuthWarning] = useState('');
  const [loading, setLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogMode, setShareDialogMode] = useState('link'); // 'link' | 'library'
  const [shareUrl, setShareUrl] = useState('');
  const [shareLoading, setShareLoading] = useState(false);

  const userToken = Cookies.get('user_token');
  const { signOut } = useAuth();

  useEffect(() => {
    if (!userToken) {
      setAuthWarning(
        t?.sb_story_session_expired || 'Your session has expired. Please sign in again.'
      );
      const signOutTimer = setTimeout(() => {
        if (signOut) signOut();
      }, 1500);

      return () => clearTimeout(signOutTimer);
    }

    setAuthWarning('');
  }, [userToken, signOut, t]);

  const loadStories = useCallback(
    async (pageToLoad) => {
      if (!userToken) return;

      const p = pageToLoad ?? 1;
      const offset = (p - 1) * PAGE_SIZE;

      setLoading(true);
      setError('');

      try {
        // Fetch 1 extra item to determine whether "Next" page exists
        const res = await axios.get(`${FASTAPI_HOST}/api/list_stories`, {
          headers: { Authorization: `Bearer ${userToken}` },
          params: {
            limit: PAGE_SIZE + 1,
            offset,
            ...(storyType ? { story_type: storyType } : {}),
          },
        });

        let fetched = res.data?.stories || [];
        const nextPageExists = fetched.length > PAGE_SIZE;
        setHasNext(nextPageExists);

        if (nextPageExists) fetched = fetched.slice(0, PAGE_SIZE);

        // If user navigated beyond available pages (e.g., data changed), go back one page.
        if (p > 1 && fetched.length === 0) {
          setPage(p - 1);
          return;
        }

        setStories(fetched);
      } catch (err) {
        if (err.response?.status === 401) {
          setStories([]);
          setHasNext(false);
          setAuthWarning(
            t?.sb_story_unauthorized ||
              'Session expired or lost. Please sign out and sign in again to load your story library.'
          );
        } else {
          setError('Error fetching stories: ' + (err.response?.data?.detail || err.message));
        }
      } finally {
        setLoading(false);
      }
    },
    [PAGE_SIZE, userToken, t, storyType]
  );

  useEffect(() => {
    loadStories(page);
  }, [page, loadStories]);

  const buildShareUrl = (shareSlug) => {
    if (!shareSlug) return '';
    return `${window.location.origin}/public/story/${shareSlug}`;
  };

  const handleShare = async (storyId, mode) => {
    if (!userToken || !storyId) return;

    // Used to display different messaging in the share popup.
    if (mode === 'link' || mode === 'library') {
      setShareDialogMode(mode);
    }

    const endpointByMode = {
      link: `${FASTAPI_HOST}/api/story/${storyId}/share`,
      library: `${FASTAPI_HOST}/api/story/${storyId}/share_to_library`,
      unshare: `${FASTAPI_HOST}/api/story/${storyId}/unshare`,
    };

    const endpoint = endpointByMode[mode];
    if (!endpoint) return;

    setShareLoading(true);
    setError('');

    try {
      const res = await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${userToken}` } });
      const { share_slug, is_public, is_listed } = res.data || {};

      setStories((prev) =>
        prev.map((s) => (s.story_id === storyId ? { ...s, share_slug, is_public, is_listed } : s))
      );

      if (is_public && share_slug) {
        setShareUrl(buildShareUrl(share_slug));
        setShareDialogOpen(true);
      } else {
        setShareUrl('');
        setShareDialogOpen(false);
      }
    } catch (err) {
      setError('Error sharing story: ' + (err.response?.data?.detail || err.message));
    } finally {
      setShareLoading(false);
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!userToken || !storyId) return;

    setLoading(true);
    setError('');

    try {
      await axios.delete(`${FASTAPI_HOST}/api/delete_story/${storyId}`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });

      setDeleteDialogOpen(false);
      setStoryToDelete(null);

      // Reload current page (keeps pagination correct)
      await loadStories(page);
    } catch (err) {
      setError('Error deleting story: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {headerContent || (showTitle && (
          <Typography
            variant="subtitle1"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              fontSize: { xs: 22, sm: 26 },
              lineHeight: 1.4,
            }}
          >
            {t?.sb_story_history || 'My Stories'}
          </Typography>
        ))}

        <StoryTypeFilter
          value={storyType}
          onChange={(nextType) => {
            setStoryType(nextType);
            setPage(1);
          }}
          t={t}
          disabled={loading}
          sx={{
            minWidth: 0,
            mb: 0,
            ml: headerContent ? 0 : 'auto',
            flex: { xs: 1, sm: '0 0 230px' },
          }}
        />

        <IconButton
          aria-label={t?.sb_refresh || 'Refresh'}
          onClick={() => loadStories(page)}
          disabled={loading}
          size="small"
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {authWarning && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          {authWarning}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Typography sx={{ mb: 2 }}>{t?.sb_loading || 'Loading...'} </Typography>}

      {!loading && userToken && stories.length === 0 && (
        <Typography sx={{ color: '#666' }}>{t?.sb_library_empty || 'No stories found for your account.'}</Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {stories.map((s) => {
          const shareStatusLabel = s?.is_public
            ? s?.is_listed
              ? t?.sb_shared_listed || 'Shared (Library)'
              : t?.sb_shared_unlisted || 'Shared (Link)'
            : t?.sb_not_shared || 'Not shared';

          return (
            <Box
              key={s.story_id}
              sx={{
                border: '1px solid #eee',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                {s.cover_img ? (
                  <Box
                    component="img"
                    src={s.cover_img}
                    alt={s.title ? `${s.title} cover` : 'Story cover'}
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 1.5,
                      objectFit: 'cover',
                      border: '1px solid #f0f0f0',
                      flexShrink: 0,
                      background: '#fafafa',
                    }}
                    loading="lazy"
                  />
                ) : (
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #f6f6f6 0%, #fafcff 100%)',
                      border: '1px solid #f0f0f0',
                      flexShrink: 0,
                    }}
                  />
                )}

                <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.title}
                </Typography>
                <Typography sx={{ color: '#555', fontSize: 13, fontWeight: 600 }}>
                  {getStoryTypeLabel(s.type || 1, t)}
                </Typography>
                  <Typography sx={{ color: '#777', fontSize: 14 }}>
                    {t?.sb_created_at || 'Created'}: {formatCreatedAt(s.created_at)}
                  </Typography>
                  <Typography sx={{ color: '#777', fontSize: 14 }}>{shareStatusLabel}</Typography>
                </Box>
              </Box>

              {/* Actions: on mobile wrap to multiple lines */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 0.75,
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  flexShrink: { xs: 1, sm: 0 },
                  maxWidth: { xs: '100%', sm: 'none' },
                  alignSelf: { xs: 'flex-end', sm: 'auto' },
                  mt: { xs: 0.5, sm: 0 },
                }}
              >
                <Tooltip title={t?.sb_open || 'Open'}>
                  <Box component="span">
                    <IconButton
                      aria-label={t?.sb_open || 'Open'}
                      onClick={() => window.open(`/story-player?storyId=${s.story_id}`, '_blank')}
                      disabled={!s.story_id}
                      size="small"
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Tooltip>

                <Tooltip title={s?.is_public ? t?.sb_copy_share_link || 'Copy share link' : t?.sb_share || 'Share link'}>
                  <Box component="span">
                    <IconButton
                      aria-label={s?.is_public ? t?.sb_copy_share_link || 'Copy share link' : t?.sb_share || 'Share link'}
                      onClick={() => {
                        if (s?.is_public && s?.share_slug) {
                          setShareDialogMode('link');
                          setShareUrl(buildShareUrl(s.share_slug));
                          setShareDialogOpen(true);
                          return;
                        }
                        handleShare(s.story_id, 'link');
                      }}
                      disabled={shareLoading}
                      size="small"
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Tooltip>
                
                {!IS_MOBILE_APP && (
                  <Tooltip title={t?.sb_share_to_library || 'Publish to Library'}>
                    <Box component="span">
                      <IconButton
                        aria-label={t?.sb_share_to_library || 'Publish to Library'}
                        onClick={() => {
                          if (s?.is_public && s?.is_listed && s?.share_slug) {
                            setShareDialogMode('library');
                            setShareUrl(buildShareUrl(s.share_slug));
                            setShareDialogOpen(true);
                            return;
                          }
                          handleShare(s.story_id, 'library');
                        }}
                        disabled={shareLoading}
                        size="small"
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                      >
                        <LibraryAddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}

                {!IS_MOBILE_APP && s?.is_public && (
                  <Tooltip title={t?.sb_unshare || 'Unshare'}>
                    <Box component="span">
                      <IconButton
                        aria-label={t?.sb_unshare || 'Unshare'}
                        onClick={() => handleShare(s.story_id, 'unshare')}
                        disabled={shareLoading}
                        size="small"
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}

                <Tooltip title={t?.sb_delete_story || 'Delete Story'}>
                  <Box component="span">
                    <IconButton
                      aria-label={t?.sb_delete_story || 'Delete Story'}
                      onClick={() => {
                        setStoryToDelete(s.story_id);
                        setDeleteDialogOpen(true);
                      }}
                      size="small"
                      color="error"
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mt: 2 }}>
        <IconButton
          aria-label={t?.sb_prev || 'Prev'}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={loading || page === 1}
          size="small"
        >
          <ChevronLeftIcon />
        </IconButton>

        <Typography
          sx={{ color: '#666', px: 1, userSelect: 'none', fontWeight: 600 }}
          aria-label={`${t?.sb_page || 'Page'} ${page}`}
        >
          {page}
        </Typography>

        <IconButton
          aria-label={t?.sb_next || 'Next'}
          onClick={() => setPage((p) => p + 1)}
          disabled={loading || !hasNext}
          size="small"
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Share dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 4 }}>
          <Typography variant="h6">
            {shareDialogMode === 'library'
              ? t?.sb_published_to_library || 'Published to Library with link:'
              : t?.sb_shared_link || 'Sharable link:'}
          </Typography>

          <Typography sx={{ wordBreak: 'break-all' }}>{shareUrl}</Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(shareUrl);
                } catch (e) {
                  setError('Failed to copy link');
                }
              }}
              disabled={!shareUrl}
            >
              {t?.sb_copy || 'Copy'}
            </Button>

            <Button variant="outlined" onClick={() => window.open(shareUrl, '_blank')} disabled={!shareUrl}>
              {t?.sb_open || 'Open'}
            </Button>

            <Button variant="outlined" color="inherit" onClick={() => setShareDialogOpen(false)}>
              {t?.sb_close || 'Close'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <Typography variant="h6" align="center" sx={{ mb: 2 }}>
            {t?.sb_confirm_delete_story || 'Confirm Delete Story'}
          </Typography>
          <Typography align="center" sx={{ mb: 2 }}>
            {t?.sb_delete_story_warning || 'Are you sure you want to delete this story? This action cannot be undone.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="contained" color="error" onClick={() => handleDeleteStory(storyToDelete)}>
              {t?.sb_delete_yes || 'Yes, Delete'}
            </Button>
            <Button variant="outlined" color="inherit" onClick={() => setDeleteDialogOpen(false)}>
              {t?.sb_delete_no || 'Cancel'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StoryHistory;
